import { getProductsByIds } from './api-shell'
import { buildStorefrontPurchaseOptionProduct } from './storefront-purchase-option'
import { resolveCheckoutPackagingInfoFromRecords } from './checkout-packaging'
import type {
  CheckoutAddressOption,
  CheckoutPaymentOption,
  CheckoutShippingOption,
  CheckoutSimulationItemTaxDetail,
} from './api'
import type { CartItem } from './types'

export type OrderCheckoutItemOverride = {
  itemId: number
  quantity: number
}

export type OrderRepurchaseCheckoutContext = {
  orderId: number | null
  freightType: 'cif' | 'fob'
  carrierId: number | null
  addressId: number | null
  street: string
  number: string
  complement: string
  neighborhood: string
  city: string
  state: string
  zipCode: string
  contactName: string
  contactPhone: string
  formaPagamentoId: number | null
  condicaoPagamentoId: number | null
  finalidade: number | null
}

const normalizeComparableText = (value: string) => value
  .trim()
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLocaleLowerCase('pt-BR')

const normalizeDigits = (value?: string | null) => (value ?? '').replace(/\D/g, '')

const splitStreetAndNumber = (street: string, number: string) => {
  const normalizedStreet = street.trim()
  const normalizedNumber = number.trim()
  if (!normalizedStreet || normalizedNumber) {
    return {
      street: normalizedStreet,
      number: normalizedNumber,
    }
  }

  const commaMatch = normalizedStreet.match(/^(.*?)[,\-]\s*([0-9A-Za-z./-]+)\s*$/)
  if (commaMatch) {
    return {
      street: commaMatch[1]?.trim() ?? normalizedStreet,
      number: commaMatch[2]?.trim() ?? '',
    }
  }

  const suffixMatch = normalizedStreet.match(/^(.*\D)\s+([0-9A-Za-z./-]+)\s*$/)
  if (suffixMatch && /\d/.test(suffixMatch[2] ?? '')) {
    return {
      street: suffixMatch[1]?.trim() ?? normalizedStreet,
      number: suffixMatch[2]?.trim() ?? '',
    }
  }

  return {
    street: normalizedStreet,
    number: normalizedNumber,
  }
}

const roundOrderCheckoutNumber = (value: number, decimals = 4) => {
  if (!Number.isFinite(value)) return 0
  const factor = 10 ** decimals
  return Math.round(value * factor) / factor
}

const isRecord = (value: unknown): value is Record<string, unknown> => (
  Boolean(value) && typeof value === 'object' && !Array.isArray(value)
)

const parsePositiveInteger = (value: unknown) => {
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
    return Math.trunc(value)
  }

  if (typeof value === 'string') {
    const normalized = value.trim()
    if (!/^\d+$/.test(normalized)) return null
    const parsed = Number.parseInt(normalized, 10)
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null
  }

  return null
}

const parseNumericValue = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value
  }

  if (typeof value === 'string') {
    const normalized = value.trim()
    if (!normalized) return null

    const sanitized = normalized
      .replace(/\s+/g, '')
      .replace(/R\$/gi, '')
      .replace(/\.(?=\d{3}(?:\D|$))/g, '')
      .replace(',', '.')
      .replace(/[^\d.-]/g, '')

    if (!sanitized) return null

    const parsed = Number.parseFloat(sanitized)
    return Number.isFinite(parsed) ? parsed : null
  }

  return null
}

const resolveItemRecords = (item: Record<string, unknown>) => {
  const candidates = [
    item,
    item.produto,
    item.product,
    item.produto_detalhe,
    item.product_detail,
  ]

  return candidates.filter((candidate): candidate is Record<string, unknown> => isRecord(candidate))
}

const resolveOrderFieldText = (record: Record<string, unknown>, keys: string[]) => {
  for (const key of keys) {
    const value = record[key]
    if (typeof value !== 'string') continue
    const normalized = value.trim()
    if (normalized) return normalized
  }

  return ''
}

const resolveOrderFieldNumber = (record: Record<string, unknown>, keys: string[]) => {
  for (const key of keys) {
    const parsed = parseNumericValue(record[key])
    if (parsed !== null) return parsed
  }

  return null
}

const parseJsonRecordCandidate = (value: unknown): unknown | null => {
  if (typeof value !== 'string') return null

  const normalized = value.trim()
  if (!normalized || !/^[{\[]/.test(normalized)) return null

  try {
    return JSON.parse(normalized)
  } catch {
    return null
  }
}

const collectPayloadRecords = (
  value: unknown,
  depth: number = 3,
  seen: Set<Record<string, unknown>> = new Set()
): Record<string, unknown>[] => {
  if (!isRecord(value) || seen.has(value)) return []
  seen.add(value)

  const records = [value]
  if (depth <= 0) return records

  Object.values(value).forEach((entry) => {
    if (Array.isArray(entry)) {
      entry.forEach((item) => {
        records.push(...collectPayloadRecords(item, depth - 1, seen))
      })
      return
    }

    if (isRecord(entry)) {
      records.push(...collectPayloadRecords(entry, depth - 1, seen))
      return
    }

    const parsedJson = parseJsonRecordCandidate(entry)
    if (Array.isArray(parsedJson)) {
      parsedJson.forEach((item) => {
        records.push(...collectPayloadRecords(item, depth - 1, seen))
      })
      return
    }

    if (isRecord(parsedJson)) {
      records.push(...collectPayloadRecords(parsedJson, depth - 1, seen))
    }
  })

  return records
}

const getFirstPayloadNumber = (payload: unknown, keys: string[]) => {
  const candidates = collectPayloadRecords(payload)

  for (const candidate of candidates) {
    for (const key of keys) {
      const parsed = parseNumericValue(candidate[key])
      if (parsed !== null) return parsed
    }
  }

  return null
}

const getFirstPayloadText = (payload: unknown, keys: string[]) => {
  const candidates = collectPayloadRecords(payload)

  for (const candidate of candidates) {
    for (const key of keys) {
      const rawValue = candidate[key]
      if (typeof rawValue === 'string' && rawValue.trim()) {
        return rawValue.trim()
      }
    }
  }

  return ''
}

const collectPayloadRecordsFromValue = (
  value: unknown,
  depth: number = 3,
  seen: Set<Record<string, unknown>> = new Set()
): Record<string, unknown>[] => {
  if (Array.isArray(value)) {
    return value.flatMap((item) => collectPayloadRecordsFromValue(item, depth, seen))
  }

  if (isRecord(value)) {
    return collectPayloadRecords(value, depth, seen)
  }

  const parsedJson = parseJsonRecordCandidate(value)
  if (Array.isArray(parsedJson)) {
    return parsedJson.flatMap((item) => collectPayloadRecordsFromValue(item, depth, seen))
  }

  if (isRecord(parsedJson)) {
    return collectPayloadRecords(parsedJson, depth, seen)
  }

  return []
}

const getFirstNumberFromRecords = (records: Record<string, unknown>[], keys: string[]) => {
  for (const record of records) {
    for (const key of keys) {
      const parsed = parseNumericValue(record[key])
      if (parsed !== null) return parsed
    }
  }

  return null
}

const getFirstTextFromRecords = (records: Record<string, unknown>[], keys: string[]) => {
  for (const record of records) {
    for (const key of keys) {
      const rawValue = record[key]
      if (typeof rawValue === 'string' && rawValue.trim()) {
        return rawValue.trim()
      }
    }
  }

  return ''
}

const collectOrderFreightPriorityRecords = (order: Record<string, unknown>) => {
  const orderPedido = isRecord(order.pedido) ? order.pedido : null
  const orderVenda = isRecord(order.venda) ? order.venda : null
  const seen = new Set<Record<string, unknown>>()

  return [
    order.nota,
    order.nota_fiscal,
    order.notaFiscal,
    orderPedido?.nota,
    orderPedido?.nota_fiscal,
    orderPedido?.notaFiscal,
    orderVenda?.nota,
    orderVenda?.nota_fiscal,
    orderVenda?.notaFiscal,
    orderPedido,
    orderVenda,
    order,
  ].flatMap((candidate) => collectPayloadRecordsFromValue(candidate, 3, seen))
}

export const resolveOrderRepurchaseFreightResponsibility = (
  order: Record<string, unknown>
): 0 | 1 | null => {
  const prioritizedFreightRecords = collectOrderFreightPriorityRecords(order)
  const freightResponsibility = getFirstNumberFromRecords(prioritizedFreightRecords, [
    'frete_por_conta',
    'fretePorConta',
    'freight_responsibility',
    'freightResponsibility',
  ]) ?? getFirstPayloadNumber(order, [
    'frete_por_conta',
    'fretePorConta',
    'freight_responsibility',
    'freightResponsibility',
  ])

  if (freightResponsibility === 1) return 1
  if (freightResponsibility === 0) return 0
  return null
}

const resolveOrderPaymentMethodId = (payload: unknown) => {
  const records = collectPayloadRecords(payload)

  for (const record of records) {
    for (const candidate of [
      record.forma_pagamento_id,
      record.formaPagamentoId,
      record.payment_method_id,
      record.paymentMethodId,
      record.metodo_pagamento_id,
      record.metodoPagamentoId,
    ]) {
      const parsed = parsePositiveInteger(candidate)
      if (parsed !== null) return parsed
    }

    for (const nestedRecord of [
      isRecord(record.forma_pagamento) ? record.forma_pagamento : null,
      isRecord(record.formaPagamento) ? record.formaPagamento : null,
      isRecord(record.payment_method) ? record.payment_method : null,
      isRecord(record.paymentMethod) ? record.paymentMethod : null,
      isRecord(record.metodo_pagamento) ? record.metodo_pagamento : null,
      isRecord(record.metodoPagamento) ? record.metodoPagamento : null,
    ]) {
      if (!nestedRecord) continue

      for (const candidate of [
        nestedRecord.forma_pagamento_id,
        nestedRecord.formaPagamentoId,
        nestedRecord.payment_method_id,
        nestedRecord.paymentMethodId,
        nestedRecord.metodo_pagamento_id,
        nestedRecord.metodoPagamentoId,
        nestedRecord.id,
      ]) {
        const parsed = parsePositiveInteger(candidate)
        if (parsed !== null) return parsed
      }
    }
  }

  return null
}

const resolveOrderPaymentConditionId = (payload: unknown) => {
  const records = collectPayloadRecords(payload)

  for (const record of records) {
    for (const candidate of [
      record.condicao_pagamento_id,
      record.condicaoPagamentoId,
      record.payment_condition_id,
      record.paymentConditionId,
      record.condicao_id,
      record.condicaoId,
    ]) {
      const parsed = parsePositiveInteger(candidate)
      if (parsed !== null) return parsed
    }

    for (const nestedRecord of [
      isRecord(record.condicao_pagamento) ? record.condicao_pagamento : null,
      isRecord(record.condicaoPagamento) ? record.condicaoPagamento : null,
      isRecord(record.payment_condition) ? record.payment_condition : null,
      isRecord(record.paymentCondition) ? record.paymentCondition : null,
      isRecord(record.condicao) ? record.condicao : null,
    ]) {
      if (!nestedRecord) continue

      for (const candidate of [
        nestedRecord.condicao_pagamento_id,
        nestedRecord.condicaoPagamentoId,
        nestedRecord.payment_condition_id,
        nestedRecord.paymentConditionId,
        nestedRecord.condicao_id,
        nestedRecord.condicaoId,
        nestedRecord.id,
      ]) {
        const parsed = parsePositiveInteger(candidate)
        if (parsed !== null) return parsed
      }
    }
  }

  return null
}

const resolveItemQuantity = (item: Record<string, unknown>) => {
  for (const record of resolveItemRecords(item)) {
    const parsed = resolveOrderFieldNumber(record, ['quantidade', 'qtde', 'quantity'])
    if (parsed !== null && parsed > 0) return parsed
  }

  return null
}

const resolveItemMoney = (item: Record<string, unknown>, keys: string[]) => {
  for (const record of resolveItemRecords(item)) {
    const parsed = resolveOrderFieldNumber(record, keys)
    if (parsed !== null) return Math.max(0, parsed)
  }

  return null
}

const resolveItemProductId = (item: Record<string, unknown>) => {
  const explicitCandidates = [
    item.produto_id,
    item.produtoId,
    item.product_id,
    item.productId,
    item.id_produto,
    isRecord(item.produto) ? item.produto.id : null,
    isRecord(item.product) ? item.product.id : null,
    isRecord(item.produto_detalhe) ? item.produto_detalhe.id : null,
    isRecord(item.product_detail) ? item.product_detail.id : null,
  ]

  for (const candidate of explicitCandidates) {
    const parsed = parsePositiveInteger(candidate)
    if (parsed !== null) return parsed
  }

  for (const record of resolveItemRecords(item)) {
    for (const candidate of [
      record.produto_id,
      record.produtoId,
      record.product_id,
      record.productId,
      record.id_produto,
    ]) {
      const parsed = parsePositiveInteger(candidate)
      if (parsed !== null) return parsed
    }
  }

  return null
}

const resolveItemProductGradeId = (item: Record<string, unknown>) => {
  const explicitCandidates = [
    item.produto_grade_id,
    item.produtoGradeId,
    item.product_grade_id,
    item.productGradeId,
    item.qtde_caixa,
    isRecord(item.produto) ? item.produto.produto_grade_id : null,
    isRecord(item.product) ? item.product.product_grade_id : null,
  ]

  for (const candidate of explicitCandidates) {
    const parsed = parsePositiveInteger(candidate)
    if (parsed !== null) return parsed
  }

  for (const record of resolveItemRecords(item)) {
    for (const candidate of [
      record.produto_grade_id,
      record.produtoGradeId,
      record.product_grade_id,
      record.productGradeId,
      record.qtde_caixa,
    ]) {
      const parsed = parsePositiveInteger(candidate)
      if (parsed !== null) return parsed
    }
  }

  return null
}

const resolveOrderItemId = (item: Record<string, unknown>) => {
  for (const candidate of [item.id, item.item_id, item.itemId, item.pedido_item_id, item.pedidoItemId]) {
    const parsed = parsePositiveInteger(candidate)
    if (parsed !== null) return parsed
  }

  return null
}

export const resolveOrderItems = (order: Record<string, unknown>) => {
  const candidates = [order.itens, order.items, order.produtos]
  for (const candidate of candidates) {
    if (Array.isArray(candidate)) {
      return candidate.filter((item): item is Record<string, unknown> => isRecord(item))
    }
  }
  return []
}

export const resolveOrderRepurchaseFreightType = (order: Record<string, unknown>): 'cif' | 'fob' => {
  const prioritizedFreightRecords = collectOrderFreightPriorityRecords(order)
  const freightResponsibility = resolveOrderRepurchaseFreightResponsibility(order)
  if (freightResponsibility === 1) return 'fob'
  if (freightResponsibility === 0) return 'cif'

  const normalized = normalizeComparableText(getFirstTextFromRecords(prioritizedFreightRecords, [
    'tipo_frete',
    'tipoFrete',
    'shipping_type',
    'shippingType',
    'modo_frete',
    'modoFrete',
    'modalidade_frete',
    'modalidadeFrete',
    'freight_type',
    'freightType',
    'frete',
  ]) || getFirstPayloadText(order, [
    'tipo_frete',
    'tipoFrete',
    'shipping_type',
    'shippingType',
    'modo_frete',
    'modoFrete',
    'modalidade_frete',
    'modalidadeFrete',
    'freight_type',
    'freightType',
    'frete',
  ]))

  if (normalized.includes('cif')) return 'cif'
  if (normalized.includes('fob')) return 'fob'

  return 'fob'
}

const resolveOrderId = (order: Record<string, unknown>) => {
  for (const candidate of [order.id, order.pedido_id, order.pedidoId]) {
    const parsed = parsePositiveInteger(candidate)
    if (parsed !== null) return parsed
  }
  return null
}

export async function buildOrderCheckoutItems(order: Record<string, unknown>) {
  const orderId = resolveOrderId(order)
  const rawItems = resolveOrderItems(order)

  const wantedIds = Array.from(
    new Set(
      rawItems
        .map((item) => resolveItemProductId(item))
        .filter((id): id is string | number => Boolean(id))
        .map((id) => String(id))
    )
  )
  const wantedBoxOptionIds = Array.from(
    new Set(
      rawItems
        .map((item) => {
          const packaging = resolveCheckoutPackagingInfoFromRecords(resolveItemRecords(item))
          if (!packaging.isBox) return null
          const productId = resolveItemProductId(item)
          return productId ? String(productId) : null
        })
        .filter((id): id is string => Boolean(id))
    )
  )
  const productsById = new Map(
    (await getProductsByIds(wantedIds, {
      fallbackMissingBoxOptionsForIds: wantedBoxOptionIds,
      resolveRequestedVariations: true,
    })).map((product) => [product.id, product])
  )

  const resolvedEntries = rawItems.map((item) => {
    const productId = resolveItemProductId(item)
    const quantity = resolveItemQuantity(item)
    const packaging = resolveCheckoutPackagingInfoFromRecords(resolveItemRecords(item))

    if (!productId || !quantity || quantity <= 0) {
      return null
    }

    const product = productsById.get(String(productId))
    if (!product) {
      console.warn(`Failed to resolve product ${productId} for order checkout.`)
      return null
    }

    const checkoutProduct = packaging.isBox
      ? buildStorefrontPurchaseOptionProduct(product, packaging.packagingQuantity)
      : product

    const subtotal = resolveItemMoney(item, [
      'preco_total',
      'precoTotal',
      'valor_total',
      'valorTotal',
      'subtotal',
      'total',
    ])
    const unitPrice = resolveItemMoney(item, [
      'preco_unitario',
      'precoUnitario',
      'valor_unitario',
      'valorUnitario',
      'unit_price',
      'unitPrice',
      'preco',
      'price',
    ])
    const ipi = resolveItemMoney(item, ['valor_ipi', 'valorIpi', 'ipi_valor', 'ipiValor', 'ipi']) ?? 0
    const difal = resolveItemMoney(item, [
      'valor_difal',
      'valorDifal',
      'difa_valor',
      'difaValor',
      'valor_st',
      'valorSt',
      'st',
    ]) ?? 0
    const taxes = resolveItemMoney(item, ['valor_impostos', 'valorImpostos', 'impostos', 'taxes'])
      ?? roundOrderCheckoutNumber(ipi + difal, 2)
    const lockedSubtotal = subtotal !== null
      ? roundOrderCheckoutNumber(subtotal, 2)
      : unitPrice !== null
        ? roundOrderCheckoutNumber(unitPrice * quantity, 2)
        : null
    const lockedUnitPrice = unitPrice !== null
      ? roundOrderCheckoutNumber(unitPrice, 9)
      : lockedSubtotal !== null && quantity > 0
        ? roundOrderCheckoutNumber(lockedSubtotal / quantity, 9)
        : null
    const lockedTaxes = roundOrderCheckoutNumber(taxes, 2)

    return {
      product: checkoutProduct,
      quantity,
      checkoutSource: 'order',
      sourceOrderId: orderId ?? undefined,
      sourceOrderItemId: resolveOrderItemId(item) ?? undefined,
      sourceProductGradeId: resolveItemProductGradeId(item) ?? undefined,
      ...(lockedUnitPrice !== null ? { lockedUnitPrice } : {}),
      ...(lockedSubtotal !== null ? { lockedSubtotal } : {}),
      lockedIpi: roundOrderCheckoutNumber(ipi, 2),
      lockedDifal: roundOrderCheckoutNumber(difal, 2),
      lockedTaxes,
      ...(lockedSubtotal !== null ? { lockedTotal: roundOrderCheckoutNumber(lockedSubtotal + lockedTaxes, 2) } : {}),
      readOnly: false,
    } satisfies CartItem
  })

  const availableEntries = resolvedEntries.filter((entry): entry is CartItem => Boolean(entry))
  return {
    availableEntries,
    unavailableCount: resolvedEntries.length - availableEntries.length,
    totalItems: rawItems.length,
  }
}

export const getOrderCheckoutItemId = (item: Pick<CartItem, 'sourceOrderItemId'>) => {
  const itemId = Number(item.sourceOrderItemId ?? 0)
  return Number.isFinite(itemId) && itemId > 0 ? Math.trunc(itemId) : null
}

export const normalizeOrderCheckoutItemOverrides = (
  overrides: OrderCheckoutItemOverride[]
): OrderCheckoutItemOverride[] => {
  const overridesByItemId = new Map<number, OrderCheckoutItemOverride>()

  for (const override of Array.isArray(overrides) ? overrides : []) {
    const itemId = Number(override?.itemId ?? 0)
    if (!Number.isFinite(itemId) || itemId <= 0) continue
    const quantity = roundOrderCheckoutNumber(Math.max(0, Number(override?.quantity ?? 0)), 4)
    overridesByItemId.set(Math.trunc(itemId), {
      itemId: Math.trunc(itemId),
      quantity,
    })
  }

  return Array.from(overridesByItemId.values()).sort((left, right) => left.itemId - right.itemId)
}

export const applyOrderCheckoutItemOverrides = (
  items: CartItem[],
  overrides: OrderCheckoutItemOverride[]
): CartItem[] => {
  const overridesByItemId = new Map(
    normalizeOrderCheckoutItemOverrides(overrides).map((override) => [override.itemId, override] as const)
  )

  return items.flatMap((item) => {
    const orderItemId = getOrderCheckoutItemId(item)
    const override = orderItemId ? overridesByItemId.get(orderItemId) : null
    if (!override) {
      return [item]
    }

    if (!(override.quantity > 0)) return []

    return [{
      ...item,
      quantity: roundOrderCheckoutNumber(override.quantity, 4),
    }]
  })
}

export const mergeOrderCheckoutSimulationItems = (
  items: CartItem[],
  simulationItems: CheckoutSimulationItemTaxDetail[]
): CartItem[] => {
  const simulationByOrderItemId = new Map<number, CheckoutSimulationItemTaxDetail>()

  for (const item of Array.isArray(simulationItems) ? simulationItems : []) {
    const orderItemId = Number(item.orderItemId ?? 0)
    if (!Number.isFinite(orderItemId) || orderItemId <= 0) continue
    simulationByOrderItemId.set(Math.trunc(orderItemId), item)
  }

  return items.map((item) => {
    const orderItemId = getOrderCheckoutItemId(item)
    const simulationItem = orderItemId ? simulationByOrderItemId.get(orderItemId) : null
    if (!simulationItem) {
      return item
    }

    const quantity = roundOrderCheckoutNumber(
      simulationItem.quantity > 0 ? simulationItem.quantity : item.quantity,
      4
    )
    const subtotal = roundOrderCheckoutNumber(Math.max(0, simulationItem.subtotal), 2)
    const ipi = roundOrderCheckoutNumber(Math.max(0, simulationItem.ipi), 2)
    const difal = roundOrderCheckoutNumber(Math.max(0, simulationItem.difal), 2)
    const discount = roundOrderCheckoutNumber(Math.max(0, simulationItem.discount), 2)
    const freight = roundOrderCheckoutNumber(Math.max(0, simulationItem.freight), 2)
    const total = roundOrderCheckoutNumber(Math.max(0, simulationItem.total), 2)
    const implicitTaxes = roundOrderCheckoutNumber(
      Math.max(0, total - Math.max(0, subtotal - discount + freight)),
      2
    )
    const impostos = roundOrderCheckoutNumber(
      Math.max(0, simulationItem.impostos || (ipi + difal) || implicitTaxes),
      2
    )

    return {
      ...item,
      quantity,
      lockedUnitPrice: quantity > 0 ? roundOrderCheckoutNumber(subtotal / quantity, 9) : item.lockedUnitPrice,
      lockedSubtotal: subtotal,
      lockedDiscount: discount,
      lockedIpi: ipi,
      lockedDifal: difal,
      lockedTaxes: impostos,
      lockedTotal: total,
      readOnly: false,
    }
  })
}

export const resolveOrderRepurchaseCheckoutContext = (order: unknown): OrderRepurchaseCheckoutContext | null => {
  if (!isRecord(order)) return null

  const explicitDeliveryAddress = isRecord(order.endereco_entrega)
    ? order.endereco_entrega
    : isRecord(order.enderecoEntrega)
      ? order.enderecoEntrega
      : null

  const addressId = parsePositiveInteger(
    explicitDeliveryAddress?.id
    ?? explicitDeliveryAddress?.endereco_id
    ?? getFirstPayloadNumber(order, [
      'endereco_entrega_id',
      'enderecoEntregaId',
      'shipping_address_id',
      'delivery_address_id',
      'endereco_id',
    ])
  )

  const street = explicitDeliveryAddress && typeof explicitDeliveryAddress.endereco === 'string'
    ? explicitDeliveryAddress.endereco.trim()
    : getFirstPayloadText(order, [
      'endereco_entrega',
      'logradouro_entrega',
      'rua_entrega',
      'shipping_address',
      'delivery_address',
      'endereco',
      'logradouro',
      'rua',
    ])
  const number = explicitDeliveryAddress && typeof explicitDeliveryAddress.numero === 'string'
    ? explicitDeliveryAddress.numero.trim()
    : getFirstPayloadText(order, [
      'numero_entrega',
      'shipping_number',
      'delivery_number',
      'numero',
    ])
  const complement = explicitDeliveryAddress && typeof explicitDeliveryAddress.complemento === 'string'
    ? explicitDeliveryAddress.complemento.trim()
    : getFirstPayloadText(order, [
      'complemento_entrega',
      'shipping_complement',
      'delivery_complement',
      'complemento',
    ])
  const neighborhood = explicitDeliveryAddress && typeof explicitDeliveryAddress.bairro === 'string'
    ? explicitDeliveryAddress.bairro.trim()
    : getFirstPayloadText(order, [
      'bairro_entrega',
      'shipping_neighborhood',
      'delivery_neighborhood',
      'bairro',
    ])
  const city = explicitDeliveryAddress && typeof explicitDeliveryAddress.cidade === 'string'
    ? explicitDeliveryAddress.cidade.trim()
    : getFirstPayloadText(order, [
      'cidade_entrega',
      'shipping_city',
      'delivery_city',
      'cidade',
    ])
  const state = (
    explicitDeliveryAddress && typeof explicitDeliveryAddress.uf === 'string'
      ? explicitDeliveryAddress.uf.trim()
      : getFirstPayloadText(order, [
        'uf_entrega',
        'shipping_state',
        'delivery_state',
        'uf',
        'estado',
      ])
  ).toUpperCase()
  const zipCode = normalizeDigits(
    explicitDeliveryAddress && typeof explicitDeliveryAddress.cep === 'string'
      ? explicitDeliveryAddress.cep.trim()
      : getFirstPayloadText(order, [
        'cep_entrega',
        'shipping_zip',
        'delivery_zip',
        'cep',
      ])
  ).slice(0, 8)

  return {
    orderId: resolveOrderId(order),
    freightType: resolveOrderRepurchaseFreightType(order),
    carrierId: parsePositiveInteger(
      order.transportadora_id
      ?? order.transportadoraId
      ?? order.shipping_carrier_id
      ?? getFirstPayloadNumber(order, [
        'transportadora_id',
        'transportadoraId',
        'shipping_carrier_id',
        'shippingCarrierId',
        'carrier_id',
        'carrierId',
      ])
      ?? (isRecord(order.transportadora) ? order.transportadora.id ?? order.transportadora.transportadora_id : null)
    ),
    addressId,
    street,
    number,
    complement,
    neighborhood,
    city,
    state,
    zipCode,
    contactName: getFirstPayloadText(order, [
      'nome_contato',
      'nomeContato',
      'contato_nome',
      'contact_name',
      'contato',
    ]),
    contactPhone: normalizeDigits(getFirstPayloadText(order, [
      'telefone_contato',
      'telefoneContato',
      'contato_telefone',
      'whatsapp_contato',
      'whatsappContato',
      'contato_whatsapp',
      'contatoWhatsapp',
      'whatsapp',
      'telefone',
      'celular',
    ])),
    formaPagamentoId: resolveOrderPaymentMethodId(order),
    condicaoPagamentoId: resolveOrderPaymentConditionId(order),
    finalidade: parsePositiveInteger(getFirstPayloadNumber(order, [
      'finalidade',
      'finalidade_id',
      'finalidadeId',
    ])),
  }
}

export const findMatchingRepurchaseAddressId = (
  addresses: CheckoutAddressOption[],
  context: OrderRepurchaseCheckoutContext | null | undefined
) => {
  if (!context || !Array.isArray(addresses) || addresses.length === 0) return null
  if (addresses.length === 1) return addresses[0].id

  if (
    typeof context.addressId === 'number'
    && Number.isFinite(context.addressId)
    && addresses.some((address) => address.id === context.addressId)
  ) {
    return context.addressId
  }

  const normalizedZipCode = normalizeDigits(context.zipCode).slice(0, 8)
  const normalizedState = normalizeComparableText(context.state)
  const normalizedCity = normalizeComparableText(context.city)
  const parsedStreetAndNumber = splitStreetAndNumber(context.street, context.number)
  const normalizedStreet = normalizeComparableText(parsedStreetAndNumber.street)
  const normalizedNumber = normalizeComparableText(parsedStreetAndNumber.number)
  const normalizedNumberDigits = normalizeDigits(parsedStreetAndNumber.number)
  const normalizedNeighborhood = normalizeComparableText(context.neighborhood)

  const exactMatches = addresses.filter((address) => (
    (!normalizedZipCode || normalizeDigits(address.zipCode).slice(0, 8) === normalizedZipCode)
    && (!normalizedState || normalizeComparableText(address.state) === normalizedState)
    && (!normalizedCity || normalizeComparableText(address.city) === normalizedCity)
    && (
      !normalizedStreet
      || normalizeComparableText(address.street) === normalizedStreet
      || normalizeComparableText(address.street).includes(normalizedStreet)
      || normalizedStreet.includes(normalizeComparableText(address.street))
    )
    && (
      !normalizedNumber
      || normalizeComparableText(address.number) === normalizedNumber
      || (normalizedNumberDigits && normalizeDigits(address.number) === normalizedNumberDigits)
    )
  ))

  if (exactMatches.length === 1) return exactMatches[0].id

  const narrowedMatches = exactMatches.filter((address) => (
    (!normalizedNeighborhood || normalizeComparableText(address.neighborhood) === normalizedNeighborhood)
  ))
  if (narrowedMatches.length === 1) return narrowedMatches[0].id

  const zipMatches = addresses.filter((address) => (
    (!normalizedZipCode || normalizeDigits(address.zipCode).slice(0, 8) === normalizedZipCode)
    && (!normalizedState || normalizeComparableText(address.state) === normalizedState)
    && (!normalizedCity || normalizeComparableText(address.city) === normalizedCity)
  ))
  if (zipMatches.length === 1) return zipMatches[0].id

  return null
}

export const findMatchingRepurchasePaymentId = (
  options: CheckoutPaymentOption[],
  context: OrderRepurchaseCheckoutContext | null | undefined
) => {
  if (!context || !Array.isArray(options) || options.length === 0) return null
  if (!context.formaPagamentoId || !context.condicaoPagamentoId) return null

  return options.find((option) => (
    option.formaPagamentoId === context.formaPagamentoId
    && option.condicaoPagamentoId === context.condicaoPagamentoId
  ))?.id ?? null
}

export const findMatchingRepurchaseShippingId = (
  options: CheckoutShippingOption[],
  context: OrderRepurchaseCheckoutContext | null | undefined
) => {
  if (!context || !Array.isArray(options) || options.length === 0) return null
  if (!context.carrierId) return null

  return options.find((option) => (
    option.transportadoraId === context.carrierId
    && option.tipoFrete === context.freightType
  ))?.id ?? null
}
